// apps/landing-page/src/hooks/useProductViewTracking.ts
import { useEffect, useRef } from 'react';
import { trackProductView, type ProductEntityType } from '@/lib/analytics';

/**
 * Fires `product.viewed` once per distinct `entityId`. A `useRef` guard (not a
 * cleanup-based one) is deliberate: React 18 Strict Mode's dev-only double
 * effect invocation reruns this on the SAME component instance without
 * resetting refs, so the guard absorbs that — but a genuine remount (leaving
 * the page and coming back) gets a fresh ref and fires again, which is a real
 * repeat view, not a duplicate.
 */
export function useProductViewTracking(
  entityType: ProductEntityType,
  entityId: string | null | undefined,
  value?: number,
  source?: string,
): void {
  const lastTracked = useRef<string | null>(null);

  useEffect(() => {
    if (!entityId || lastTracked.current === entityId) return;
    lastTracked.current = entityId;
    trackProductView(entityType, entityId, value, source);
    // entityType/value/source intentionally excluded from deps: they describe the same
    // entityId's product, not a reason to re-fire — only a new entityId is.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);
}
